import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Building2, Users, Telescope } from "lucide-react";
import { useParams } from "react-router";
import { useAtomValue } from "jotai";
import { teamApi, type Team } from "../api/client";
import { localeAtom } from "../stores/locale";
import { t } from "../lib/i18n";
import { useTeamScope } from "../hooks/useTeamScope";

// TeamScopeSwitcher sits under the GalaxySwitcher and narrows every galaxy view
// to a department → team scope (?team=). It's a SINGLE hierarchical dropdown
// (departments as headers, their teams indented) rather than two stacked
// selects, so the control never grows a second row on selection. Only
// suggested-and-approved or manual/github teams appear (a pending suggestion
// isn't a real scope yet).
export default function TeamScopeSwitcher() {
  const { account = "" } = useParams<{ account: string }>();
  const { teamId, setTeamId } = useTeamScope();
  const locale = useAtomValue(localeAtom);
  const [teams, setTeams] = useState<Team[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!account) return;
    let live = true;
    teamApi
      .tree(account)
      .then((t) => {
        if (live) setTeams(t || []);
      })
      .catch((e) => console.error("team tree failed", e));
    return () => {
      live = false;
    };
  }, [account]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Only teams that participate in scoping (a suggested team is hidden until
  // approved).
  const live = teams.filter((t) => t.source !== "suggested" || t.approved_at);
  const departments = live.filter((t) => t.parent_team_id == null);
  const teamsByDept = (deptId: number) => live.filter((t) => t.parent_team_id === deptId);

  // Nothing to scope by yet — keep the chrome quiet (org with no defined teams
  // just reads at galaxy scope).
  if (departments.length === 0) return null;

  const allLabel = t("teamScopeSwitcher.all", locale);
  const selected = teamId != null ? live.find((t) => t.id === teamId) : undefined;
  const buttonLabel = selected?.name ?? allLabel;
  const ButtonIcon = !selected ? Telescope : selected.parent_team_id == null ? Building2 : Users;

  const choose = (id: number | undefined) => {
    setTeamId(id);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative px-md pb-md">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-sm rounded-lg border border-gruvbox-bg3/60 bg-gruvbox-bg0/40 px-sm py-xs text-xs text-gruvbox-fg1 transition-colors hover:border-gruvbox-aqua/40 hover:text-gruvbox-fg"
      >
        <ButtonIcon size={13} className="shrink-0 text-gruvbox-gray" />
        <span className="flex-1 truncate text-left">{buttonLabel}</span>
        <ChevronDown size={13} className={`shrink-0 text-gruvbox-gray transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-3 right-3 z-[60] mt-xs max-h-[20rem] overflow-y-auto rounded-lg border border-gruvbox-bg3 bg-gruvbox-bg1 py-xs shadow-2xl ring-1 ring-gruvbox-bg0">
          {/* All engineers (whole galaxy) */}
          <button
            onClick={() => choose(undefined)}
            className={`flex w-full items-center gap-sm px-sm py-xs text-xs transition-colors ${
              teamId == null ? "text-gruvbox-aqua" : "text-gruvbox-fg1 hover:text-gruvbox-fg"
            }`}
          >
            <span className="flex w-3 shrink-0 items-center justify-center text-gruvbox-aqua">
              {teamId == null && <Check size={11} />}
            </span>
            <Telescope size={12} className="shrink-0 text-gruvbox-gray" />
            <span className="truncate">{allLabel}</span>
          </button>

          {departments.map((dept) => (
            <div key={dept.id}>
              {/* Department row (selectable = whole department) */}
              <button
                onClick={() => choose(dept.id)}
                className={`flex w-full items-center gap-sm px-sm py-xs text-xs transition-colors ${
                  teamId === dept.id ? "text-gruvbox-aqua" : "text-gruvbox-fg1 hover:text-gruvbox-fg"
                }`}
              >
                <span className="flex w-3 shrink-0 items-center justify-center text-gruvbox-aqua">
                  {teamId === dept.id && <Check size={11} />}
                </span>
                <Building2 size={12} className="shrink-0 text-gruvbox-gray" />
                <span className="truncate font-medium">{dept.name}</span>
              </button>
              {/* Teams under the department, indented */}
              {teamsByDept(dept.id).map((team) => (
                <button
                  key={team.id}
                  onClick={() => choose(team.id)}
                  className={`flex w-full items-center gap-sm py-xs pl-lg pr-sm text-xs transition-colors ${
                    teamId === team.id ? "text-gruvbox-aqua" : "text-gruvbox-fg1 hover:text-gruvbox-fg"
                  }`}
                >
                  <span className="flex w-3 shrink-0 items-center justify-center text-gruvbox-aqua">
                    {teamId === team.id && <Check size={11} />}
                  </span>
                  <Users size={12} className="shrink-0 text-gruvbox-gray" />
                  <span className="truncate">{team.name}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
